import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2, Users, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DeleteSelectedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMessageIds: Set<string>;
  userId: string;
  conversationId: string;
  canDeleteForEveryone: boolean;
  onDeleteComplete: (deletedIds: string[], forEveryone: boolean) => void;
  onDeleted?: () => void;
}

export function DeleteSelectedDialog({
  open,
  onOpenChange,
  selectedMessageIds,
  userId,
  conversationId,
  canDeleteForEveryone,
  onDeleteComplete,
  onDeleted,
}: DeleteSelectedDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const count = selectedMessageIds.size;

  const handleDeleteForMe = async () => {
    if (count === 0) return;
    setIsDeleting(true);

    const ids = Array.from(selectedMessageIds);
    let successCount = 0;

    for (const messageId of ids) {
      // Get current hidden_for array
      const { data: msgData } = await supabase
        .from("messages")
        .select("hidden_for")
        .eq("id", messageId)
        .maybeSingle();

      const currentHiddenFor = (msgData?.hidden_for as string[]) || [];
      
      const { error } = await supabase
        .from("messages")
        .update({ hidden_for: [...currentHiddenFor, userId] })
        .eq("id", messageId);

      if (!error) successCount++;
    }

    setIsDeleting(false);
    onOpenChange(false);
    onDeleteComplete(ids, false);
    onDeleted?.();
    
    if (successCount === count) {
      toast.success(`${count} message${count > 1 ? "s" : ""} supprimé${count > 1 ? "s" : ""} pour vous`);
    } else {
      toast.error(`Erreur: ${count - successCount} message(s) non supprimé(s)`);
    }
  };

  const handleDeleteForEveryone = async () => {
    if (count === 0) return;
    setIsDeleting(true);

    const ids = Array.from(selectedMessageIds);
    const now = new Date().toISOString();

    const { error } = await supabase
      .from("messages")
      .update({
        is_deleted: true,
        deleted_at: now,
        deleted_by: userId,
      })
      .in("id", ids);

    setIsDeleting(false);
    onOpenChange(false);
    onDeleteComplete(ids, true);
    onDeleted?.();

    if (error) {
      toast.error("Erreur lors de la suppression");
    } else {
      toast.success(`${count} message${count > 1 ? "s" : ""} supprimé${count > 1 ? "s" : ""} pour tout le monde`);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[360px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 size={18} />
            Supprimer {count} message{count > 1 ? "s" : ""}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            Choisissez comment supprimer {count > 1 ? "ces messages" : "ce message"}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2 py-2">
          <button
            onClick={handleDeleteForMe}
            disabled={isDeleting}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left disabled:opacity-50"
          >
            {isDeleting ? (
              <Loader2 size={18} className="shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Trash2 size={18} className="shrink-0 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm font-medium">Supprimer pour moi</p>
              <p className="text-xs text-muted-foreground">
                {count > 1 ? "Ces messages seront masqués" : "Ce message sera masqué"} uniquement pour vous
              </p>
            </div>
          </button>
          {canDeleteForEveryone && (
            <button
              onClick={handleDeleteForEveryone}
              disabled={isDeleting}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-destructive/10 transition-colors text-left disabled:opacity-50"
            >
              {isDeleting ? (
                <Loader2 size={18} className="shrink-0 animate-spin text-destructive" />
              ) : (
                <Users size={18} className="shrink-0 text-destructive" />
              )}
              <div>
                <p className="text-sm font-medium text-destructive">
                  Supprimer pour tout le monde
                </p>
                <p className="text-xs text-muted-foreground">
                  {count > 1 ? "Ces messages seront supprimés" : "Ce message sera supprimé"} pour tous les participants
                </p>
              </div>
            </button>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
